import { Project } from "pmt-types/src/models/Project.ts";
import { useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import DeleteConfirmationModal from "../../components/app/deleteConfirmationModal.tsx";
import SourceDetailsCard from "../../components/app/sourceDetailsCard.tsx";
import SourceProjectList from "../../components/app/sourceProjectList.tsx";
import ErrorLabel from "../../components/form/errorLabel.tsx";
import LoadingSpinner from "../../components/loadingSpinner.tsx";
import PageHeader from "../../components/pageHeader.tsx";
import NotFoundPage from "../../global/notFoundPage.tsx";
import { useMutationDeleteProject } from "../../hooks/mutations/useMutationDeleteProject.ts";
import { useMutationDeleteSource } from "../../hooks/mutations/useMutationDeleteSource.ts";
import { QueryKeys } from "../../hooks/queries/queryKeys.ts";
import { useQueryGetProjectsForSourceId } from "../../hooks/queries/useQueryGetProjectsForSourceId.ts";
import { useQueryGetSourceById } from "../../hooks/queries/useQueryGetSourceById.ts";
import { Maybe } from "../../utils/types/maybe.ts";

export default function SourceDetails() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { sourceId } = useParams();

  const { data: source, isFetching: isFetchingSource } =
    useQueryGetSourceById(sourceId);
  const { data: projects, isFetching: isFetchingProjects } =
    useQueryGetProjectsForSourceId(sourceId);

  const {
    mutate: deleteProject,
    isLoading: isProjectDeleting,
    error: projectDeleteError,
  } = useMutationDeleteProject();

  const {
    mutate: deleteSource,
    isLoading: isSourceDeleting,
    error: sourceDeleteError,
  } = useMutationDeleteSource();

  const [deletingProject, setDeletingProject] = useState<Maybe<Project>>(null);

  const [isProjectDeleteModalOpen, setIsProjectDeleteModalOpen] =
    useState(false);
  const [isSourceDeleteModalOpen, setIsSourceDeleteModalOpen] = useState(false);

  if (sourceId == null) {
    return <NotFoundPage />;
  }

  if (isFetchingSource || isFetchingProjects) {
    return <LoadingSpinner />;
  }

  if (source == null || projects == null) {
    return <NotFoundPage />;
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-12 h-full overflow-y-auto">
        <PageHeader title={"Source Details"} />
        {sourceDeleteError != null && (
          <ErrorLabel message={sourceDeleteError?.toString()} />
        )}
        <div className="mt-12 flex flex-col gap-12">
          <SourceDetailsCard
            source={source}
            onEdit={() => navigate(`/sources/${sourceId}`)}
            onDelete={() => {
              setIsSourceDeleteModalOpen(true);
            }}
          />
          <SourceProjectList
            projects={projects}
            onAddProject={() =>
              navigate(`/sources/${sourceId}/projects/new?return=source`)
            }
            onViewProject={(project) =>
              navigate(`/sources/${sourceId}/projects/${project.id}/details`)
            }
            onEditProject={(project) => {
              navigate(
                `/sources/${sourceId}/projects/${project.id}?return=source`,
              );
            }}
            onDeleteProject={(project) => {
              setDeletingProject(project);
              setIsProjectDeleteModalOpen(true);
            }}
          />
          <DeleteConfirmationModal
            entityName="project"
            isOpen={isProjectDeleteModalOpen}
            isLoading={isProjectDeleting}
            onConfirm={() => {
              if (deletingProject == null) {
                setIsProjectDeleteModalOpen(false);
                return;
              }

              deleteProject(
                {
                  sourceId: source.id,
                  projectId: deletingProject.id,
                },
                {
                  onSuccess: () => {
                    queryClient.invalidateQueries([
                      QueryKeys.GetSourceProjects,
                      source.id,
                    ]);
                    setDeletingProject(null);
                    setIsProjectDeleteModalOpen(false);
                  },
                  onError: (error) =>
                    console.error("Error deleting project", error),
                },
              );
            }}
            onClose={() => setIsProjectDeleteModalOpen(false)}
          />
          <DeleteConfirmationModal
            entityName="source"
            isOpen={isSourceDeleteModalOpen}
            isLoading={isSourceDeleting}
            onConfirm={() => {
              if (source == null) {
                setIsSourceDeleteModalOpen(false);
                return;
              }

              deleteSource(
                {
                  sourceId: source.id,
                },
                {
                  onSuccess: () => {
                    setIsSourceDeleteModalOpen(false);
                    navigate("/sources");
                  },
                  onError: (error) =>
                    console.error("Error deleting source", error),
                },
              );
            }}
            onClose={() => setIsSourceDeleteModalOpen(false)}
          />
          {projectDeleteError != null && (
            <ErrorLabel message={projectDeleteError?.toString()} />
          )}
        </div>
      </div>
    </div>
  );
}
